import React, { useState } from 'react';
import Api from '../services/api';
import { useResource } from '../hooks/useResource';
import { T, Icon, useText } from '../components/Text';
import Chart, { Distribution, Ranking, palette } from '../components/Chart';

const shiftDays = (date, days) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Metrikler ve İstatistikler Sayfası (/admin.html#metrics, /metrics)
 * Sipariş akışı, müşteri yoğunluğu, ayar & renk dağılımı grafiklerini sunar.
 */
export default function Metrics() {
  const { t, locale } = useText();

  const [period, setPeriod] = useState('30');
  const [dates, setDates] = useState({
    from: shiftDays(Api.today(), -29),
    to: Api.today(),
  });
  const [appliedDates, setAppliedDates] = useState(dates);
  const [activeTab, setActiveTab] = useState('orders');
  const [trendType, setTrendType] = useState('bar');
  const [pieType, setPieType] = useState('donut');
  const [customerQuery, setCustomerQuery] = useState('');
  const [error, setError] = useState('');

  const queryString =
    period === 'all' ? '' : '?' + new URLSearchParams(appliedDates).toString();
  const resource = useResource(`/api/order-summary${queryString}`, [
    'orders-changed',
    'customers-changed',
  ]);
  const data = resource.data;

  const formatNumber = (n) => new Intl.NumberFormat(locale).format(n || 0);
  const formatDate = (val) =>
    val ? new Date(`${val}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const formatPercent = (val) =>
    new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format((val || 0) / 100);

  const handlePeriodChange = (val) => {
    setPeriod(val);
    setError('');
    if (val === 'all') return;

    const today = Api.today();
    const range = {
      from:
        val === 'today'
          ? today
          : val === 'month'
          ? today.slice(0, 8) + '01'
          : shiftDays(today, -(Number(val) - 1)),
      to: today,
    };
    setDates(range);
    setAppliedDates(range);
  };

  const handleCustomDateSubmit = (e) => {
    e.preventDefault();
    if (dates.from > dates.to) {
      setError('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }
    setError('');
    setPeriod('custom');
    setAppliedDates(dates);
  };

  const statusData = data
    ? [
        { label: 'Tamamlanan', value: data.summary.completed },
        { label: 'İşlemde', value: data.summary.pending },
      ]
    : [];
  const statusColors = ['#8faf87', '#ca7e72'];

  const colorRows = data?.colors || [];
  const karatRows = data?.karats || [];
  const customColorMap = {
    Yeşil: '#8faf87',
    Beyaz: '#b5b7b1',
    Roz: '#ce948c',
    Diğer: '#aa9bcb',
    Belirtilmedi: '#8a9085',
  };
  const colorPalette = colorRows.map((r) => customColorMap[r.label] || '#dbb36c');

  const filteredCustomers = (data?.customers || []).filter((r) =>
    r.name.toLocaleLowerCase('tr-TR').includes(customerQuery.toLocaleLowerCase('tr-TR'))
  );

  const bestPeriod = data?.timeline?.reduce(
    (best, row) => (row.total > (best?.total || 0) ? row : best),
    null
  );

  return (
    <section id="metrics" className="analytics" aria-labelledby="metrics-title">
      {/* Üst Başlık & Şube Bilgisi */}
      <div className="analytics-heading">
        <div>
          <div className="eyebrow">
            <T>ÜRETİME BİR BAKIŞ</T>
          </div>
          <h1 id="metrics-title">
            <T>Metrikler</T>
          </h1>
          <p>
            <T>Siparişleriniz, müşterileriniz ve üretim dağılımınız.</T>
          </p>
        </div>
        <div className="analytics-branch">
          <Icon name="shop" />
          <span id="analytics-branch-name">
            {data?.branch?.name ? `${data.branch.name} Şubesi` : 'Şube'}
          </span>
          <small>
            <T>Şubeye özel veriler</T>
          </small>
        </div>
      </div>

      {/* Kontroller: Dönem Seçimi ve Tarih Aralığı */}
      <div className="analytics-controls">
        <div className="analytics-periods" role="group" aria-label={t('Metrik tarih aralığı')}>
          {['today', '7', '30', 'month', 'all'].map((p) => {
            const labels = {
              today: 'Bugün',
              7: 'Son 7 gün',
              30: 'Son 30 gün',
              month: 'Bu ay',
              all: 'Tüm zamanlar',
            };
            return (
              <button
                key={p}
                type="button"
                data-period={p}
                aria-pressed={period === p}
                className={period === p ? 'selected' : ''}
                onClick={() => handlePeriodChange(p)}
              >
                <T>{labels[p]}</T>
              </button>
            );
          })}
        </div>

        <form id="analytics-dates" onSubmit={handleCustomDateSubmit}>
          <label>
            <T>Başlangıç</T>
            <input
              type="date"
              id="analytics-from"
              required
              min="2000-01-01"
              max="9999-12-31"
              value={dates.from}
              onChange={(e) => setDates((prev) => ({ ...prev, from: e.target.value }))}
            />
          </label>
          <label>
            <T>Bitiş</T>
            <input
              type="date"
              id="analytics-to"
              required
              min="2000-01-01"
              max="9999-12-31"
              value={dates.to}
              onChange={(e) => setDates((prev) => ({ ...prev, to: e.target.value }))}
            />
          </label>
          <button type="submit" data-icon="filter">
            <Icon name="filter" />
            <T>Uygula</T>
          </button>
        </form>
      </div>

      {/* Tarih Aralığı Satırı & Canlı Güncelleme Zamanı */}
      <div className="analytics-range-line">
        <span id="analytics-range">
          {data
            ? `${formatDate(data.range.from)} — ${formatDate(data.range.to)}`
            : resource.error
            ? 'Seçili dönemin verileri görüntülenemedi.'
            : 'Veriler yükleniyor…'}
        </span>
        <span id="analytics-update" role="status" aria-live="polite">
          {data ? (
            <T>
              {`Güncellendi · ${new Date(data.generatedAt).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
              })}`}
            </T>
          ) : null}
        </span>
      </div>

      {(error || resource.error) && (
        <p className="form-error" id="analytics-error" role="alert">
          <T>{error || resource.error}</T>
        </p>
      )}

      {/* 4 Özet Kartı */}
      <div className="analytics-summary" aria-label={t('Seçili dönemin özeti')}>
        <article>
          <Icon name="sheets" />
          <small>
            <T>SİPARİŞ SAYISI</T>
          </small>
          <strong id="analytics-total">{data ? formatNumber(data.summary.total) : '—'}</strong>
          <p id="analytics-customer-count">
            {data ? (
              <T>{`${formatNumber(data.summary.customers)} müşterinin siparişi var`}</T>
            ) : (
              '—'
            )}
          </p>
        </article>

        <article className="analytics-ready">
          <Icon name="checkmark" />
          <small>
            <T>TAMAMLANAN</T>
          </small>
          <strong id="analytics-completed">
            {data ? formatNumber(data.summary.completed) : '—'}
          </strong>
          <p>
            <T>Teslime hazır siparişler</T>
          </p>
        </article>

        <article className="analytics-pending">
          <Icon name="hourglass" />
          <small>
            <T>İŞLEMDE</T>
          </small>
          <strong id="analytics-pending">
            {data ? formatNumber(data.summary.pending) : '—'}
          </strong>
          <p id="analytics-unassigned">
            {data?.summary?.unassigned ? (
              <T>{`${formatNumber(data.summary.unassigned)} siparişte müşteri seçilmedi`}</T>
            ) : (
              <T>Devam eden siparişler</T>
            )}
          </p>
        </article>

        <article className="analytics-rate">
          <Icon name="combo" />
          <small>
            <T>TAMAMLANMA ORANI</T>
          </small>
          <strong id="analytics-rate">{data ? formatPercent(data.summary.rate) : '—'}</strong>
          <p id="analytics-rate-note">
            {data ? (
              <T>{`${data.summary.completed} / ${data.summary.total} sipariş hazır`}</T>
            ) : (
              '—'
            )}
          </p>
        </article>
      </div>

      {/* Sekmeler: Siparişler, Müşteriler, Ayar & Renk */}
      <div className="analytics-tabs" role="tablist" aria-label={t('Metrik bölümleri')}>
        <button
          type="button"
          role="tab"
          id="analytics-tab-orders"
          aria-selected={activeTab === 'orders'}
          className={activeTab === 'orders' ? 'selected' : ''}
          onClick={() => setActiveTab('orders')}
        >
          <Icon name="combo" />
          <T>Siparişler</T>
        </button>
        <button
          type="button"
          role="tab"
          id="analytics-tab-customers"
          aria-selected={activeTab === 'customers'}
          className={activeTab === 'customers' ? 'selected' : ''}
          onClick={() => setActiveTab('customers')}
        >
          <Icon name="customers" />
          <T>Müşteriler</T>
        </button>
        <button
          type="button"
          role="tab"
          id="analytics-tab-categories"
          aria-selected={activeTab === 'categories'}
          className={activeTab === 'categories' ? 'selected' : ''}
          onClick={() => setActiveTab('categories')}
        >
          <Icon name="sheets" />
          <T>Ayar & Renk</T>
        </button>
      </div>

      {/* 1. SEKME: SİPARİŞLER */}
      {activeTab === 'orders' && (
        <div id="analytics-orders" role="tabpanel" aria-labelledby="analytics-tab-orders">
          {/* Sipariş Akışı Çizgi / Sütun Grafiği */}
          <article className="analytics-card analytics-trend-card">
            <div className="analytics-card-heading">
              <div>
                <div className="eyebrow">
                  <T>SİPARİŞ AKIŞI</T>
                </div>
                <h2>
                  <T>Üretimin ritmi</T>
                </h2>
                <p id="analytics-trend-description">
                  {data ? (
                    <T>
                      {data.range.granularity === 'year'
                        ? 'Yıllara göre tamamlanan ve işlemdeki siparişler.'
                        : data.range.monthly
                        ? 'Aylara göre tamamlanan ve işlemdeki siparişler.'
                        : 'Günlere göre tamamlanan ve işlemdeki siparişler.'}
                    </T>
                  ) : null}
                </p>
              </div>
              <label className="analytics-chart-choice">
                <T>Grafik</T>
                <select
                  id="analytics-trend-type"
                  value={trendType}
                  onChange={(e) => setTrendType(e.target.value)}
                >
                  <option value="bar">{t('Sütun')}</option>
                  <option value="line">{t('Çizgi')}</option>
                </select>
              </label>
            </div>

            <div className="analytics-legend">
              <span className="legend-ready">
                <T>Tamamlanan</T>
              </span>
              <span className="legend-pending">
                <T>İşlemde</T>
              </span>
              <span className="legend-gold">
                <T>Toplam</T>
              </span>
            </div>

            <div className="analytics-chart" data-chart="timeline">
              <Chart rows={data?.timeline || []} type={trendType} label="Sipariş akışı grafiği" />
            </div>

            <p className="analytics-chart-caption" id="analytics-trend-caption">
              <T>
                {bestPeriod
                  ? `En yoğun dönem: ${bestPeriod.date} · ${formatNumber(bestPeriod.total)} sipariş`
                  : 'Seçili tarih aralığında sipariş bulunmuyor.'}
              </T>
            </p>
          </article>

          {/* İki Kolon: Sipariş Dengesi (Donut) & En Yoğun Müşteriler */}
          <div className="analytics-two-columns">
            <article className="analytics-card">
              <div className="analytics-card-heading">
                <div>
                  <div className="eyebrow">
                    <T>GENEL DURUM</T>
                  </div>
                  <h2>
                    <T>Sipariş dengesi</T>
                  </h2>
                </div>
              </div>
              <div className="analytics-chart analytics-round-chart" data-chart="status">
                <Chart
                  rows={statusData}
                  type="donut"
                  colors={statusColors}
                  label="Tamamlanan ve işlemdeki siparişlerin dağılımı"
                />
              </div>
              <div className="analytics-distribution" id="analytics-status-legend">
                <Distribution rows={statusData} colors={statusColors} />
              </div>
            </article>

            <article className="analytics-card">
              <div className="analytics-card-heading">
                <div>
                  <div className="eyebrow">
                    <T>EN YOĞUN MÜŞTERİLER</T>
                  </div>
                  <h2>
                    <T>Üretimde öne çıkanlar</T>
                  </h2>
                  <p>
                    <T>Seçili dönemde en çok siparişi olan ilk 5 müşteri.</T>
                  </p>
                </div>
              </div>
              <div id="analytics-top-customers" className="analytics-ranking">
                <Ranking
                  rows={(data?.customers || []).filter((r) => r.id !== null).slice(0, 5)}
                />
              </div>
            </article>
          </div>
        </div>
      )}

      {/* 2. SEKME: MÜŞTERİLER */}
      {activeTab === 'customers' && (
        <div id="analytics-customers" role="tabpanel" aria-labelledby="analytics-tab-customers">
          <article className="analytics-card">
            <div className="analytics-card-heading">
              <div>
                <div className="eyebrow">
                  <T>MÜŞTERİ YOĞUNLUĞU</T>
                </div>
                <h2>
                  <T>Birlikte üretiyoruz.</T>
                </h2>
                <p>
                  <T>Yalnızca seçili dönemde siparişi olan müşteriler.</T>
                </p>
              </div>
              <label className="analytics-search">
                <Icon name="search" />
                <input
                  id="analytics-customer-search"
                  type="search"
                  placeholder={t('Müşteri ara…')}
                  aria-label={t('Metriklerde müşteri ara')}
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                />
              </label>
            </div>

            <div className="analytics-legend">
              <span className="legend-ready">
                <T>Tamamlanan</T>
              </span>
              <span className="legend-pending">
                <T>İşlemde</T>
              </span>
            </div>

            <div
              id="analytics-customer-ranking"
              className="analytics-ranking analytics-full-ranking"
            >
              <Ranking rows={filteredCustomers.slice(0, 10)} />
            </div>

            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <caption>
                  <T>Müşterilere göre sipariş durumu</T>
                </caption>
                <thead>
                  <tr>
                    <th scope="col">
                      <T>Müşteri</T>
                    </th>
                    <th scope="col">
                      <T>Toplam</T>
                    </th>
                    <th scope="col">
                      <T>Tamamlanan</T>
                    </th>
                    <th scope="col">
                      <T>İşlemde</T>
                    </th>
                    <th scope="col">
                      <T>Oran</T>
                    </th>
                  </tr>
                </thead>
                <tbody id="analytics-customer-table">
                  {filteredCustomers.map((r) => (
                    <tr key={r.id || 'unassigned'}>
                      <td>{r.id ? r.name : <T>Müşteri Seçilmedi</T>}</td>
                      <td>{formatNumber(r.total)}</td>
                      <td>{formatNumber(r.completed)}</td>
                      <td>{formatNumber(r.pending)}</td>
                      <td>{formatPercent(r.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        </div>
      )}

      {/* 3. SEKME: AYAR & RENK */}
      {activeTab === 'categories' && (
        <div id="analytics-categories" role="tabpanel" aria-labelledby="analytics-tab-categories">
          <div className="analytics-category-toolbar">
            <span>
              <T>Üretiminizin ayar ve renk dağılımı.</T>
            </span>
            <label className="analytics-chart-choice">
              <T>Grafik</T>
              <select
                id="analytics-pie-type"
                value={pieType}
                onChange={(e) => setPieType(e.target.value)}
              >
                <option value="donut">{t('Halka')}</option>
                <option value="pie">{t('Pasta')}</option>
              </select>
            </label>
          </div>

          <div className="analytics-two-columns">
            {/* Ayar Dağılımı */}
            <article className="analytics-card">
              <div className="analytics-card-heading">
                <div>
                  <div className="eyebrow">
                    <T>AYAR DAĞILIMI</T>
                  </div>
                  <h2>
                    <T>Üretimin ayarı</T>
                  </h2>
                </div>
              </div>
              <div className="analytics-chart analytics-round-chart" data-chart="karats">
                <Chart
                  rows={karatRows}
                  type={pieType}
                  colors={palette}
                  label="Ayarlara göre sipariş dağılımı"
                />
              </div>
              <div className="analytics-distribution" id="analytics-karat-legend">
                <Distribution rows={karatRows} colors={palette} />
              </div>
            </article>

            {/* Renk Dağılımı */}
            <article className="analytics-card">
              <div className="analytics-card-heading">
                <div>
                  <div className="eyebrow">
                    <T>RENK DAĞILIMI</T>
                  </div>
                  <h2>
                    <T>Renklerin payı</T>
                  </h2>
                </div>
              </div>
              <div className="analytics-chart analytics-round-chart" data-chart="colors">
                <Chart
                  rows={colorRows}
                  type={pieType}
                  colors={colorPalette}
                  label="Renklere göre sipariş dağılımı"
                />
              </div>
              <div className="analytics-distribution" id="analytics-color-legend">
                <Distribution rows={colorRows} colors={colorPalette} />
              </div>
            </article>
          </div>

          {/* Ayar & Renk Kombinasyonları */}
          <article className="analytics-card">
            <div className="analytics-card-heading">
              <div>
                <div className="eyebrow">
                  <T>AYAR & RENK</T>
                </div>
                <h2>
                  <T>En sık üretilen kombinasyonlar</T>
                </h2>
                <p>
                  <T>Seçili dönemin tüm ayar ve renk eşleşmeleri.</T>
                </p>
              </div>
            </div>
            <div id="analytics-combinations" className="analytics-ranking analytics-combinations">
              <Ranking rows={data?.combinations || []} total={data?.summary?.total} />
            </div>
          </article>
        </div>
      )}

      <p className="analytics-footnote">
        <T>
          Her fotoğraflı sipariş bir kayıt olarak sayılır. Grafikler seçili şube ve sipariş tarihine
          göre hesaplanır.
        </T>
      </p>
    </section>
  );
}
